import { DealResult } from '@/lib/search-utils';
import { SquareArrowOutUpRight, ChevronDown, Package, UserCircle, Pencil, Check } from 'lucide-react';
import { format } from 'date-fns';
import { ClosingDatePickerContent } from '@/components/crm/ClosingDatePickerContent';
import { DealCompaniesSection } from '@/components/crm/DealCompaniesSection';
import { Button } from '@/components/ui/button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { useCallback, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactsListResponse, BoardResponse } from '@shared/api';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  buildMeasurementMapsUrl,
  isPreciseMeasurementAddress,
  resolveDealGeo,
} from '@/lib/measurement-location';
import { getCurrentLang, getTranslatedName } from '@/lib/translation-utils';
import { getDateFnsLocale } from '@/lib/date-fns-locale';
import { dealClosingDayIso, closingDateDisplayClass } from '@/lib/crm-closing-date';
import { getDealInstallationDateFromRow } from '@/lib/crm-deal-stage';
import { InstallationDateDialog } from '@/components/crm/InstallationDateDialog';
import { isInstallationDateCaptureStageRow } from '../../../functions/lib/deal-installation-date';

const DEAL_DETAIL_DATA_CARD =
  'rounded-lg border border-border bg-card p-5 text-sm motion-reduce:animate-none';

/** Above search Dialog (z-50); may extend past the modal edges. */
const SEARCH_DETAIL_POPOVER_CONTENT_CLASS = 'z-[200]';

export default function DealSearchDetail({
  deal,
  onOpenDeal,
}: {
  deal: DealResult;
  onOpenDeal: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'information' | 'notes' | 'email'>('information');
  const [editClosingDate, setEditClosingDate] = useState(false);
  const [closingDateValue, setClosingDateValue] = useState('');
  const [pendingWonStageId, setPendingWonStageId] = useState<string | null>(null);
  const [installationConfirming, setInstallationConfirming] = useState(false);
  const dateLocale = getDateFnsLocale(i18n.language);
  const qc = useQueryClient();

  const dealQuery = useQuery({
    queryKey: ['deal', deal.id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${deal.id}`), {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Deal fetch failed');
      const data = await r.json();
      return data.deal || data;
    },
    retry: 1,
  });

  const contactsQuery = useQuery({
    queryKey: queryKeys.crmContacts,
    queryFn: async () => {
      const r = await fetch(apiUrl('/api/crm/contacts'), {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Contacts fetch failed');
      const data = await r.json() as ContactsListResponse;
      return data.contacts;
    },
    retry: 1,
  });

  // Only fetch notes when the notes tab is active to avoid unnecessary requests
  const notesQuery = useQuery({
    queryKey: ['deal-notes', deal.id],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${deal.id}/notes`), {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Notes fetch failed');
      const data = await r.json();
      return data.notes || [];
    },
    enabled: activeTab === 'notes', // Only fetch when notes tab is active
    retry: 1,
  });

  const boardQuery = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async () => {
      const r = await fetch(apiUrl('/api/crm/board'), {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Board fetch failed');
      const data = await r.json() as BoardResponse;
      return data;
    },
    retry: 1,
  });

  const updateStageMutation = useMutation({
    mutationFn: async ({ stageId, installationDate }: { stageId: string; installationDate?: string }) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${deal.id}/stage?_=${Date.now()}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          stageId,
          ...(installationDate ? { installationDate } : {}),
        }),
      });
      if (!r.ok) throw new Error('Stage update failed');
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
      qc.invalidateQueries({ queryKey: ['deal', deal.id] });
    },
    onError: (error: any) => {
      console.error('❌ Stage update error:', error.message || error);
    },
  });

  const updateClosingDateMutation = useMutation({
    mutationFn: async (closingDate: string | null) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${deal.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ closingDate }),
      });
      if (!r.ok) throw new Error('Closing date update failed');
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal', deal.id] });
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
    },
    onError: (error: any) => {
      console.error('❌ Closing date update error:', error.message || error);
    },
  });

  const changePipeline = useMutation({
    mutationFn: async (pipelineId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${deal.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pipelineId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(
          (body as any)?.details?.message ??
            (body as any)?.message ??
            t('deal.pipelineChangeFailed'),
        );
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal', deal.id] });
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
    },
    onError: (error: any) => {
      console.error('❌ Pipeline update error:', error.message || error);
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async (body: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${deal.id}/notes`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body }),
      });
      if (!r.ok) throw new Error('Jegyzet mentése sikertelen');
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal-notes', deal.id] });
    },
  });

  const d = dealQuery.data || deal;
  const requestStageMove = (stageId: string) => {
    const targetStage = (boardQuery.data?.stages ?? []).find((s: any) => s.id === stageId);
    const dealRow = d as unknown as Record<string, unknown>;
    const currentStageId = dealRow?.stageId ?? dealRow?.stage_id;
    if (
      targetStage &&
      isInstallationDateCaptureStageRow(targetStage as unknown as Record<string, unknown>) &&
      stageId !== currentStageId &&
      !getDealInstallationDateFromRow(dealRow as unknown as Record<string, unknown>)
    ) {
      setPendingWonStageId(stageId);
      return;
    }
    updateStageMutation.mutate({ stageId });
  };
  const measurementProducts = d?.measurementData?.products ?? d?.measurement_data?.products ?? [];
  const dealContact = d?.contactId
    ? (contactsQuery.data || []).find((c: any) => c.id === d.contactId)
    : null;
  const currentStage = d?.stageId
    ? (boardQuery.data?.stages || []).find((s: any) => s.id === d.stageId)
    : null;

  // Only show stages that belong to the deal's (sub)pipeline
  const dealPipelineId = d?.pipelineId || d?.pipeline_id;
  const stagesForDealPipeline = (boardQuery.data?.stages || []).filter(
    (s: any) => !dealPipelineId || (s.pipelineId || s.pipeline_id) === dealPipelineId
  ).sort((a: any, b: any) => (a.order ?? a.order_index ?? a.orderIndex ?? 0) - (b.order ?? b.order_index ?? b.orderIndex ?? 0));

  // Subpipeline info for selector
  const pipelines = boardQuery.data?.pipelines || [];
  const dealPipeline = dealPipelineId ? pipelines.find((p: any) => p.id === dealPipelineId) : null;
  const isActuallySubPipeline = dealPipeline && (dealPipeline.parentPipelineId ?? dealPipeline.parent_pipeline_id);
  const subPipeline = isActuallySubPipeline ? dealPipeline : null;
  const parentPipelineId = subPipeline
    ? (subPipeline.parentPipelineId ?? subPipeline.parent_pipeline_id)
    : null;
  const availableSubPipelinesForSelect = parentPipelineId
    ? pipelines.filter(
        (p: any) =>
          (p.parentPipelineId ?? p.parent_pipeline_id) === parentPipelineId,
      )
    : [];

  const lang = getCurrentLang(i18n.language);
  const currentStageName = currentStage
    ? getTranslatedName(currentStage, lang) || (currentStage as { name?: string }).name || '—'
    : d?.stageId
      ? String(d.stageId)
      : '—';

  const showFetchShell = dealQuery.isLoading && !dealQuery.data;
  const dealId = String(d?.id ?? deal.id);

  const handleOpenFullPage = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onOpenDeal();
    },
    [onOpenDeal],
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" data-search-detail-panel>
      {showFetchShell ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 space-y-4 border-b border-border p-5 sm:p-6">
            <div className="text-xl font-semibold text-foreground sm:text-2xl">{deal.title}</div>
            <div className="h-4 w-32 rounded-md bg-muted motion-reduce:animate-none animate-pulse" />
            <div className="h-10 max-w-xs rounded-lg bg-muted/70 motion-reduce:animate-none animate-pulse" />
            <div className="h-9 w-48 rounded-full bg-muted/60 motion-reduce:animate-none animate-pulse" />
          </div>
          <div className="flex shrink-0 gap-6 border-b border-border px-4 sm:px-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-10 w-24 rounded-md bg-muted/50 motion-reduce:animate-none animate-pulse"
              />
            ))}
          </div>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain p-5 sm:p-6">
            <div className="h-28 rounded-lg border border-border bg-muted/25 motion-reduce:animate-none animate-pulse" />
            <div className="h-36 rounded-lg border border-border bg-muted/20 motion-reduce:animate-none animate-pulse" />
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          </div>
        </div>
      ) : (
        <>
        <div className="flex min-h-0 flex-1 flex-col overflow-visible">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-visible overscroll-contain">
            <div className="sticky top-0 z-20 border-b border-border bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
      {/* Header — shadcn outline gombok, bezárás a modál fejlécében */}
      <div className="bg-background p-5 sm:p-6">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <h2
              className={cn(
                'break-words font-semibold tracking-tight text-foreground',
                (d?.title ?? deal.title).length > 42 ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl',
              )}
            >
              {d?.title ?? deal.title}
            </h2>
            {d?.ownerName ? (
              <p className="mt-1.5 text-xs text-muted-foreground">{d.ownerName}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="relative z-30 h-9 w-9 shrink-0 sm:h-10 sm:w-10"
            onClick={handleOpenFullPage}
            title={t('search.openFullPageTitle')}
            aria-label={t('search.openFullPageTitle')}
          >
            <SquareArrowOutUpRight className="h-4 w-4 shrink-0" aria-hidden />
          </Button>
        </div>

        {/* Deal metadata: Sub-Pipeline, Etapa, Dátum uzavretia (vízszintes sor) */}
        <div className="mb-4 space-y-2 text-sm text-foreground/70">
          <div className="flex flex-wrap items-center gap-4">
            {(subPipeline || availableSubPipelinesForSelect.length > 0) && (
              <div className="flex items-center gap-2">
                <span className="font-medium">{t('deal.subPipeline')}</span>
                {subPipeline ? (
                  availableSubPipelinesForSelect.length > 1 ? (
                    <Popover modal={false}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="-mx-0.5 inline-flex items-center gap-1 rounded px-0.5 py-0.5 font-normal text-inherit hover:bg-muted/50"
                          aria-label={t('deal.edit')}
                        >
                          <span>{getTranslatedName(subPipeline, lang) || d.subPipelineName || subPipeline.name}</span>
                          <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className={cn('w-56 p-0', SEARCH_DETAIL_POPOVER_CONTENT_CLASS)}
                        align="start"
                      >
                        <Command>
                          <CommandInput placeholder={t('deal.search')} />
                          <CommandList>
                            <CommandEmpty>{t('deal.noResults')}</CommandEmpty>
                            <CommandGroup>
                              {availableSubPipelinesForSelect.map((p: any) => (
                                <CommandItem
                                  key={p.id}
                                  value={getTranslatedName(p, lang)}
                                  onSelect={() => changePipeline.mutate(p.id)}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4 flex-shrink-0',
                                      subPipeline?.id === p.id ? 'opacity-100' : 'opacity-0',
                                    )}
                                  />
                                  <span className="truncate">{getTranslatedName(p, lang)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <span>{getTranslatedName(subPipeline, lang) || d.subPipelineName || subPipeline.name}</span>
                  )
                ) : availableSubPipelinesForSelect.length > 0 ? (
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="-mx-0.5 inline-flex items-center gap-1 rounded px-0.5 py-0.5 font-normal text-inherit hover:bg-muted/50"
                        aria-label={t('deal.edit')}
                      >
                        <span>—</span>
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className={cn('w-56 p-0', SEARCH_DETAIL_POPOVER_CONTENT_CLASS)}
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder={t('deal.search')} />
                        <CommandList>
                          <CommandEmpty>{t('deal.noResults')}</CommandEmpty>
                          <CommandGroup>
                            {availableSubPipelinesForSelect.map((p: any) => (
                              <CommandItem
                                key={p.id}
                                value={getTranslatedName(p, lang)}
                                onSelect={() => changePipeline.mutate(p.id)}
                              >
                                <Check className="mr-2 h-4 w-4 flex-shrink-0 opacity-0" />
                                <span className="truncate">{getTranslatedName(p, lang)}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <span>—</span>
                )}
              </div>
            )}

            {d?.stageId ? (
              <div className="flex items-center gap-2">
                <span className="font-medium">{t('deal.stageLabel')}</span>
                {stagesForDealPipeline.length > 0 ? (
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="-mx-0.5 inline-flex items-center gap-1 rounded px-0.5 py-0.5 font-normal text-inherit hover:bg-muted/50"
                        aria-label={t('deal.edit')}
                      >
                        <span>{currentStageName}</span>
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className={cn('w-56 p-0', SEARCH_DETAIL_POPOVER_CONTENT_CLASS)}
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder={t('deal.search')} />
                        <CommandList>
                          <CommandEmpty>{t('deal.noResults')}</CommandEmpty>
                          <CommandGroup>
                            {boardQuery.isLoading ? (
                              <div className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</div>
                            ) : stagesForDealPipeline.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-muted-foreground">{t('deal.noStagesFound')}</div>
                            ) : (
                              stagesForDealPipeline.map((s: any) => (
                                <CommandItem
                                  key={s.id}
                                  value={getTranslatedName(s, lang)}
                                  onSelect={() => requestStageMove(s.id)}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4 flex-shrink-0',
                                      (d.stageId || d.stage_id) === s.id ? 'opacity-100' : 'opacity-0',
                                    )}
                                  />
                                  <span className="truncate">{getTranslatedName(s, lang)}</span>
                                </CommandItem>
                              ))
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <span>{currentStageName}</span>
                )}
              </div>
            ) : null}

            <div className="flex items-center gap-2">
              <span className="font-medium">{t('deal.closingDate')}</span>
              <Popover
                modal={false}
                open={editClosingDate}
                onOpenChange={(open) => {
                  setEditClosingDate(open);
                  if (open) {
                    setClosingDateValue(
                      d?.closingDate ? d.closingDate.split('T')[0] : format(new Date(), 'yyyy-MM-dd'),
                    );
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="group -mx-1 flex items-center gap-2 rounded px-1 py-0.5 hover:bg-muted/50"
                    aria-label={t('deal.edit')}
                  >
                    <span
                      className={cn(
                        d?.closingDate ? closingDateDisplayClass(dealClosingDayIso(d)) : 'text-muted-foreground',
                      )}
                    >
                      {d?.closingDate
                        ? new Date(d.closingDate).toLocaleDateString('hu-HU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : '—'}
                    </span>
                    <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  unconstrained
                  className={cn(
                    'rounded-lg border shadow-lg',
                    SEARCH_DETAIL_POPOVER_CONTENT_CLASS,
                  )}
                  align="end"
                  side="bottom"
                  sideOffset={6}
                  collisionPadding={12}
                  avoidCollisions
                >
                  <ClosingDatePickerContent
                    value={closingDateValue}
                    onChange={setClosingDateValue}
                    onCancel={() => {
                      setEditClosingDate(false);
                      setClosingDateValue(d?.closingDate ? d.closingDate.split('T')[0] : '');
                    }}
                    onSave={() => {
                      updateClosingDateMutation.mutate(closingDateValue || null, {
                        onSuccess: () => {
                          setEditClosingDate(false);
                        },
                        onError: (error: unknown) => {
                          const errorMessage = error instanceof Error ? error.message : String(error);
                          alert(t('deal.errorPrefix') + errorMessage);
                        },
                      });
                    }}
                    saving={updateClosingDateMutation.isPending}
                    locale={dateLocale}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-t border-border/70 bg-background px-4 sm:px-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('information')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'information'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('deal.tabInformation')}
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'notes'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('deal.tabNotes')}
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'email'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('deal.tabEmail')}
          </button>
        </div>
      </div>
            </div>

        {activeTab === 'information' && (
          <div className="mx-auto w-full max-w-2xl space-y-8 p-5 sm:p-6">
            {/* Súvisiaci kontakt, Cégek, Termékek */}
            <div className="space-y-0">
              {/* 1. Súvisiaci kontakt */}
              <div>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <UserCircle className="h-4 w-4 shrink-0" />
                  {t('deal.relatedContactHeading')}
                </h3>
                {dealContact ? (
                  <div className={cn(DEAL_DETAIL_DATA_CARD, 'space-y-0')}>
                    <div className="font-semibold text-foreground mb-3">{dealContact.name}</div>
                    <div className="space-y-2">
                      {dealContact.email && (
                        <div className="flex justify-between gap-3 items-start">
                          <span className="text-muted-foreground shrink-0">{t('deal.email')}</span>
                          <a
                            className="text-right font-medium text-primary hover:underline break-all min-w-0"
                            href={`mailto:${dealContact.email}`}
                          >
                            {dealContact.email}
                          </a>
                        </div>
                      )}
                      {dealContact.phone && (
                        <div className="flex justify-between gap-3 items-start">
                          <span className="text-muted-foreground shrink-0">{t('deal.phone')}</span>
                          <a
                            className="text-right font-medium text-primary hover:underline break-all"
                            href={`tel:${dealContact.phone}`}
                          >
                            {dealContact.phone}
                          </a>
                        </div>
                      )}
                      {dealContact.city && (
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground shrink-0">{t('deal.city')}</span>
                          <span className="text-right font-medium text-foreground">{dealContact.city}</span>
                        </div>
                      )}
                      {dealContact.street && (
                        <div className="flex justify-between gap-3 items-start">
                          <span className="text-muted-foreground shrink-0">{t('deal.street')}</span>
                          <span className="text-right font-medium text-foreground break-words min-w-0">
                            {dealContact.street}
                          </span>
                        </div>
                      )}
                      {(dealContact as { company?: string }).company?.trim() && (
                        <div className="flex justify-between gap-3 items-start">
                          <span className="text-muted-foreground shrink-0">{t('deal.companyLabel')}</span>
                          <span className="text-right font-medium text-foreground break-words min-w-0">
                            {(dealContact as { company?: string }).company}
                          </span>
                        </div>
                      )}
                      {dealContact.ico && (
                        <div className="flex justify-between gap-3">
                          <span className="text-muted-foreground shrink-0">{t('deal.icoLabel')}</span>
                          <span className="text-right font-medium text-foreground tabular-nums">{dealContact.ico}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-foreground/60">—</div>
                )}
              </div>

              {/* 2. Cégek — nem összecsukható */}
              <div className="mt-6">
                <DealCompaniesSection dealId={deal.id} />
              </div>

              {/* 3. Termékek */}
              <div className="mt-6 border-t border-border pt-6">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Package className="h-4 w-4 shrink-0" />
                  {t('deal.productsHeading')}
                </h3>
                {measurementProducts.length > 0 ? (
                  <div className="space-y-3">
                    {measurementProducts.map((p: any, idx: number) => (
                      <div key={p.id || idx} className={cn(DEAL_DETAIL_DATA_CARD, 'space-y-0')}>
                        <div className="mb-3">
                          <div className="font-semibold text-foreground">{p.productType} {p.index}</div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {[
                              p.widths?.length && p.heights?.length
                                ? `${p.widths.join(', ')} × ${p.heights.join(', ')} mm`
                                : p.widths?.length
                                  ? `${p.widths.join(', ')} mm`
                                  : p.heights?.length
                                    ? `${p.heights.join(', ')} mm`
                                    : null,
                              p.surface?.trim() || null,
                              p.color?.trim() || null,
                              p.prelis?.trim() || null,
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between gap-3 items-start">
                            <span className="text-muted-foreground shrink-0">{t('prices.testerWidth', 'Szélesség (mm)')}</span>
                            <span className="text-right font-medium text-foreground">{p.widths?.length ? p.widths.join(', ') : '—'}</span>
                          </div>
                          <div className="flex justify-between gap-3 items-start">
                            <span className="text-muted-foreground shrink-0">{t('prices.testerHeight', 'Magasság (mm)')}</span>
                            <span className="text-right font-medium text-foreground">{p.heights?.length ? p.heights.join(', ') : '—'}</span>
                          </div>
                          <div className="flex justify-between gap-3 items-start">
                            <span className="text-muted-foreground shrink-0">{t('gateMeasurementExtra.surfaceLabel', 'Povrch')}</span>
                            <span className="text-right font-medium text-foreground">{p.surface ?? '—'}</span>
                          </div>
                          <div className="flex justify-between gap-3 items-start">
                            <span className="text-muted-foreground shrink-0">{t('gateMeasurementExtra.colorLabel', 'Farba')}</span>
                            <span className="text-right font-medium text-foreground">{p.color ?? '—'}</span>
                          </div>
                          <div className="flex justify-between gap-3 items-start">
                            <span className="text-muted-foreground shrink-0">{t('gateMeasurementExtra.prelisLabel', 'Prelis')}</span>
                            <span className="text-right font-medium text-foreground">{p.prelis ?? '—'}</span>
                          </div>
                          <div className="border-t border-border pt-3 mt-1">
                            <span className="text-muted-foreground block mb-1 text-sm">{t('gateMeasurementExtra.notesLabel', 'Megjegyzés')}</span>
                            <div className="font-medium text-foreground whitespace-pre-wrap">{p.notes?.trim() || '—'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground/60">—</p>
                )}
                {(d?.address || resolveDealGeo(d as any)) && (
                  <div className="border-t border-border pt-3">
                    <div className="mb-1 text-xs font-semibold text-foreground/70">
                      {t('deal.measurementLocationLabel', 'Megmérési hely')}
                    </div>
                    {(() => {
                      const geo = resolveDealGeo(d as any);
                      const mapsUrl = buildMeasurementMapsUrl({
                        ...(d as any),
                        address: d?.address,
                      });
                      const label =
                        d?.address || (geo ? `${geo.lat}, ${geo.lng}` : '');
                      return mapsUrl ? (
                        <a
                          className="break-all text-sm text-primary hover:underline"
                          href={mapsUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ) : (
                        <span className="break-all text-sm">{label}</span>
                      );
                    })()}
                    {d?.address &&
                      resolveDealGeo(d as any) &&
                      !isPreciseMeasurementAddress(d.address) && (
                        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                          {t('deal.coarseMeasurementAddressHint')}
                        </p>
                      )}
                  </div>
                )}
                {(() => {
                  const geo = resolveDealGeo(d as any);
                  if (!geo) return null;
                  return (
                    <div className="flex justify-between pt-1 text-sm">
                      <span className="text-muted-foreground">{t('deal.coordinates')}</span>
                      <a
                        className="text-primary hover:underline"
                        href={buildMeasurementMapsUrl({ geoLat: geo.lat, geoLng: geo.lng }) || '#'}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {geo.lat}, {geo.lng}
                      </a>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="mx-auto w-full max-w-2xl space-y-4 p-5 sm:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const body = (
                  e.currentTarget.elements.namedItem('note') as HTMLInputElement
                ).value;
                if (body.trim()) {
                  addNoteMutation.mutate(body);
                  (
                    e.currentTarget.elements.namedItem('note') as HTMLInputElement
                  ).value = '';
                }
              }}
              className="flex gap-2"
            >
              <input
                name="note"
                className="flex-1 px-3 py-2 rounded-md border bg-background text-sm"
                placeholder={t("search.newNotePlaceholder")}
              />
              <button
                type="submit"
                disabled={addNoteMutation.isPending}
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
              >
                {t("deal.addActivity")}
              </button>
            </form>

            {notesQuery.isLoading ? (
              <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
            ) : notesQuery.data && notesQuery.data.length > 0 ? (
              <ul className="space-y-3">
                {notesQuery.data
                  .slice()
                  .sort((a: any, b: any) =>
                    (b.updatedAt || b.createdAt).localeCompare(
                      a.updatedAt || a.createdAt
                    )
                  )
                  .map((n: any) => (
                    <li key={n.id} className="p-3 border border-border rounded-md bg-card">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-foreground">
                          {n.editorName || n.authorName || n.authorEmail}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(n.updatedAt || n.createdAt).toLocaleDateString('hu-HU')}
                        </span>
                      </div>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{n.body}</p>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">{t('deal.noNotesYet')}</p>
            )}
          </div>
        )}

        {activeTab === 'email' && (
          <div className="mx-auto flex min-h-64 w-full max-w-2xl flex-col items-center justify-center p-6">
            <div className="text-center">
              <p className="mb-4 text-sm text-muted-foreground">
                {t('deal.emailHistoryPlaceholder')}
              </p>
              <button
                type="button"
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                {t('deal.connectEmail')}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
        </>
      )}

      <InstallationDateDialog
        open={!!pendingWonStageId}
        onOpenChange={(open) => {
          if (!open) setPendingWonStageId(null);
        }}
        dealTitle={d?.title ?? deal.title}
        confirming={installationConfirming}
        onConfirm={async (installationDate) => {
          if (!pendingWonStageId) return;
          setInstallationConfirming(true);
          try {
            updateStageMutation.mutate({ stageId: pendingWonStageId, installationDate });
            setPendingWonStageId(null);
          } finally {
            setInstallationConfirming(false);
          }
        }}
      />
    </div>
  );
}

